import React, { useState } from 'react';
import { WaitlistInitialData } from './WaitlistModal';

interface WaitlistSectionProps {
  onOpenWaitlist?: (tab?: 'customer' | 'vendor' | 'rider', data?: WaitlistInitialData) => void;
}

export const WaitlistSection: React.FC<WaitlistSectionProps> = ({ onOpenWaitlist }) => {
  const [selectedRole, setSelectedRole] = useState<'customer' | 'vendor' | 'rider'>('customer');

  const handleOpenModal = (role?: 'customer' | 'vendor' | 'rider') => {
    const targetRole = role || selectedRole;
    if (onOpenWaitlist) {
      onOpenWaitlist(targetRole);
    }
  };

  return (
    <section className="relative py-20 bg-gradient-to-br from-emerald-900 via-emerald-800 to-slate-900 text-white overflow-hidden">
      {/* Background Glow */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
        <div className="space-y-3 max-w-3xl mx-auto">
          <span className="inline-block px-4 py-1.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 text-xs font-extrabold uppercase tracking-wider">
            Early Access &amp; VIP Onboarding
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight">
            Be The First to Experience <span className="text-orange-400">MarketMate</span>
          </h2>
          <p className="text-sm sm:text-base text-emerald-100/80 leading-relaxed">
            Whether you buy fresh foodstuff, sell produce, or deliver orders, join our early access waitlist to receive priority launch access.
          </p>
        </div>

        {/* Role Selector Buttons */}
        <div className="inline-flex flex-wrap justify-center p-1.5 rounded-2xl bg-black/40 border border-emerald-500/30 gap-1.5 shadow-inner">
          <button
            type="button"
            onClick={() => handleOpenModal('customer')}
            className={`px-5 py-3 rounded-xl text-xs sm:text-sm font-extrabold transition-all cursor-pointer ${selectedRole === 'customer'
              ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
              : 'text-slate-300 hover:text-white hover:bg-white/10'
              }`}
            onMouseEnter={() => setSelectedRole('customer')}
          >
            I Want to Buy (Customer)
          </button>
          <button
            type="button"
            onClick={() => handleOpenModal('vendor')}
            className={`px-5 py-3 rounded-xl text-xs sm:text-sm font-extrabold transition-all cursor-pointer ${selectedRole === 'vendor'
              ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30'
              : 'text-slate-300 hover:text-white hover:bg-white/10'
              }`}
            onMouseEnter={() => setSelectedRole('vendor')}
          >
            I Want to Sell (Merchant / Farmer)
          </button>
          <button
            type="button"
            onClick={() => handleOpenModal('rider')}
            className={`px-5 py-3 rounded-xl text-xs sm:text-sm font-extrabold transition-all cursor-pointer ${selectedRole === 'rider'
              ? 'bg-sky-600 text-white shadow-lg shadow-sky-600/30'
              : 'text-slate-300 hover:text-white hover:bg-white/10'
              }`}
            onMouseEnter={() => setSelectedRole('rider')}
          >
            I Want to Deliver (Rider)
          </button>
        </div>

        {/* Action Button */}
        <div className="pt-2">
          <button
            type="button"
            onClick={() => handleOpenModal(selectedRole)}
            className={`px-9 py-4 rounded-full font-extrabold text-base text-white shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer ${selectedRole === 'customer'
              ? 'bg-emerald-500 hover:bg-emerald-600 shadow-emerald-500/40'
              : selectedRole === 'rider'
                ? 'bg-sky-600 hover:bg-sky-700 shadow-sky-600/40'
                : 'bg-orange-500 hover:bg-orange-600 shadow-orange-500/40'
              }`}
          >
            Join Early Access Waitlist →
          </button>
        </div>
      </div>
    </section>
  );
};

